import { motion } from "motion/react";
import {
  Bell,
  CheckCircle2,
  ClipboardList,
  Heart,
  HeartHandshake,
  ListChecks,
  MessageCircle,
  Sparkles,
  Tag,
  TreePine,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { ModuleId } from "@/pages/AppShell";
import type { Notification, NotificationKind } from "./notifications";

/* ─────────────────────  Kind metadata  ───────────────────── */

const KIND_META: Record<
  NotificationKind,
  { label: string; icon: typeof Bell; tint: string }
> = {
  chat: {
    label: "家人聊天室",
    icon: MessageCircle,
    tint: "from-rose-50 to-rose-100/60",
  },
  status: {
    label: "FamStatus",
    icon: CheckCircle2,
    tint: "from-emerald-50 to-emerald-100/60",
  },
  // The DB enum `notification_kind = '... | mood | ...'` predates
  // the merge and is preserved verbatim (the SQL enum can't be
  // trimmed without a migration). On the panel we surface them
  // under the merged FamStatus brand with a 心情 tag so users
  // don't see an orphan "FamMood" label.
  mood: {
    label: "FamStatus · 心情",
    icon: Heart,
    tint: "from-violet-50 to-violet-100/60",
  },
  list: {
    label: "FamList",
    icon: ListChecks,
    tint: "from-amber-50 to-amber-100/60",
  },
  tree: {
    label: "FamTree",
    icon: TreePine,
    tint: "from-sky-50 to-sky-100/60",
  },
  system: {
    label: "系統",
    icon: Sparkles,
    tint: "from-zinc-50 to-zinc-100/60",
  },
  match: {
    label: "FamMatch",
    icon: HeartHandshake,
    tint: "from-pink-50 to-pink-100/60",
  },
  tag: {
    label: "標籤",
    icon: Tag,
    tint: "from-cyan-50 to-cyan-100/60",
  },
};

/* ─────────────────────  View  ───────────────────── */

type Props = {
  items: Notification[];
  unreadCount: number;
  onMarkAllRead: () => void;
  onMarkRead: (id: string) => void;
  onNavigate: (id: ModuleId) => void;
  onClose: () => void;
};

export function NotificationsPanel({
  items,
  unreadCount,
  onMarkAllRead,
  onMarkRead,
  onNavigate,
  onClose,
}: Props) {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <span
          aria-hidden
          className="grid h-14 w-14 place-items-center rounded-2xl bg-secondary text-foreground/60"
        >
          <Bell className="h-6 w-6" aria-hidden />
        </span>
        <div>
          <div className="text-sm font-semibold">暫時冇新通知</div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            屋企人有新動態時，會即刻喺度顯示。
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {unreadCount > 0 && (
        <div className="mb-3 flex items-center justify-between rounded-2xl bg-secondary/40 px-3 py-2">
          <span className="text-xs text-muted-foreground">
            {unreadCount} 則未讀
          </span>
          <button
            type="button"
            onClick={onMarkAllRead}
            className="text-xs font-medium text-primary hover:underline"
          >
            全部標記為已讀
          </button>
        </div>
      )}

      <ul className="space-y-2">
        {items.map((n) => {
          const meta = KIND_META[n.kind];
          const Icon = meta.icon;
          return (
            <li key={n.id}>
              <motion.button
                type="button"
                onClick={() => {
                  onMarkRead(n.id);
                  onNavigate(n.target);
                  onClose();
                }}
                whileTap={{ scale: 0.98 }}
                className={cn(
                  "flex w-full items-start gap-3 rounded-2xl border border-border/40 p-3 text-left transition-colors hover:bg-secondary",
                  n.unread && "border-primary/30 bg-primary/5",
                )}
              >
                {n.who ? (
                  <span
                    aria-hidden
                    className={cn(
                      "grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br text-sm font-semibold text-white",
                      n.who.color,
                    )}
                  >
                    {n.who.initials}
                  </span>
                ) : (
                  <span
                    aria-hidden
                    className={cn(
                      "grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-gradient-to-br text-foreground/85",
                      meta.tint,
                    )}
                  >
                    <Icon className="h-5 w-5" aria-hidden />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    {n.who && (
                      <>
                        <span className="font-medium text-foreground/85">
                          {n.who.name}
                        </span>
                        <span>·</span>
                      </>
                    )}
                    <span className="inline-flex items-center gap-1">
                      <Icon className="h-3 w-3" aria-hidden />
                      {meta.label}
                    </span>
                  </div>
                  <div
                    className={cn(
                      "mt-0.5 text-sm",
                      n.unread ? "font-semibold" : "font-medium",
                    )}
                  >
                    {n.title}
                  </div>
                  <div className="line-clamp-2 text-xs text-muted-foreground">
                    {n.body}
                  </div>
                  <div className="mt-1 text-[10px] text-muted-foreground/80">
                    {n.time}
                  </div>
                </div>
                {n.unread && (
                  <span
                    aria-hidden
                    className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary"
                  />
                )}
              </motion.button>
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        onClick={() => {
          onNavigate("today");
          onClose();
        }}
        className="mt-4 inline-flex h-10 w-full items-center justify-center rounded-full border border-border/60 bg-card text-sm font-medium text-foreground/85 hover:bg-secondary"
      >
        <ClipboardList className="mr-1.5 h-4 w-4" aria-hidden />
        睇今日總覽
      </button>
    </div>
  );
}
